import axios from "axios";
import store from "../store";
import { getEnvironment } from "../utils";
import { ASO_TSEC_EXPIRATION } from "../config/constants";
import { DO_DATA_AUTH_ASO } from "@/config/constants/action-types";

const environment = getEnvironment();
const apiBaseUrls = {
  prod: "https://aso-live-online.bbva.com.ar",
  qa: "https://aso-int-ar.work-02.platform.bbva.com",
  de: "https://aso-int-ar.work-02.platform.bbva.com"
};
const ASO_API_BASE = apiBaseUrls[environment.name] || apiBaseUrls.de;

const getFlowIdByCamp = camp => {
  switch (camp) {
    case "ofertaCajaAhorro":
      return "00010";
    case "ofertaCGU":
      return "00011";
    case "ofertaEmprendedores":
      return "00014";
    case "ofertaNomina":
      return "00018";
    default:
      return "00010";
  }
};

/**
 * Objeto de configuración para Veridas
 * Este objeto contiene la configuración necesaria para la integración con Veridas
 * incluyendo los modos de operación, configuraciones de UI y flujos de validación.
 * @type {Object}
 */
export const veridasConfigData = {
  flowId: "",
  platform: "WEB",
  configData: {
    operationMode: "SMGG20220122",
    setup: {
      loading: {
        text: "El proceso está en marcha, por favor esperá...",
        backgroundColor: "#FFFFFF",
        textColor: "#072146",
        progressIndicator: { backgroundColor: "#FFFFFF", color: "#072146" }
      },
      errorModal: {
        show: true,
        generic: {
          title: "Error",
          message: "Ocurrió un error, intentá más tarde."
        },
        connection: {
          title: "Error",
          message: "Ocurrió un error, intentá más tarde."
        },
        selfie: {
          title: "Error",
          message: "No pudimos analizar el rostro. Por favor, volvé a intentar"
        },
        document: {
          generic: {
            title: "Error",
            message:
              "No pudimos analizar el documento. Por favor, volvé a intentar"
          }
        }
      },
      error: {
        backgroundColor: "#FFFFFF",
        containerBox: { show: false },
        text: "Se produjo un error. No es posible continuar con la validación",
        textColor: "#b92a45",
        iconColor: "#b92a45"
      },
      success: {
        backgroundColor: "#FFFFFF",
        containerBox: { show: false },
        text: "",
        textColor: "#072146",
        iconColor: "#072146"
      }
    },
    flowSetup: {
      stages: ["document", "selfie"],
      options: {
        document: {
          captures: [
            {
              documentTypes: ["AR_IDCard_2012"],
              setup: {
                generic: {
                  common: {
                    ui: {
                      buttons: { close: { show: true } }
                    }
                  }
                },
                sdkDocument: {
                  views: {
                    deviceRotated: {
                      onOrientation: "portrait",
                      active: false
                    },
                    instructions: {
                      active: true,
                      buttons: { startProcess: { text: "Empezar" } },
                      card: {
                        title: {
                          documents: ["Verificá tu documento"],
                          documentsBack: [
                            "Girá el documento y mostrá la parte de atrás"
                          ]
                        },
                        subtitle: {
                          documents: [
                            "Ubicá tu documento en el rectángulo y la captura se hará de forma automática"
                          ],
                          documentsBack: [
                            "Mostrá la parte trasera de tu documento a la cámara"
                          ]
                        }
                      }
                    },
                    capture: {
                      successTick: { show: false },
                      manualCapture: {
                        obverse: {
                          card: { title: "Mantené la cámara estable" }
                        },
                        reverse: {
                          card: { title: "Mantené la cámara estable" }
                        }
                      },
                      centered: {
                        obverse: {
                          card: { title: "Mantené la cámara estable" }
                        },
                        reverse: {
                          card: { title: "Mantené la cámara estable" }
                        }
                      },
                      tooFar: {
                        obverse: { card: { title: "Acercá el documento" } },
                        reverse: { card: { title: "Acercá el documento" } }
                      },
                      tooClose: {
                        obverse: { card: { title: "Alejá el documento" } },
                        reverse: { card: { title: "Alejá el documento" } }
                      },
                      notCentered: {
                        obverse: { card: { title: "Centrá el documento" } },
                        reverse: { card: { title: "Centrá el documento" } }
                      },
                      guidance: {
                        preCapture: {
                          obverse: {
                            card: {
                              header: "",
                              title:
                                "Ubicá el FRENTE de tu documento en el rectángulo",
                              short: "Encuadrá el FRENTE del documento"
                            }
                          },
                          reverse: {
                            card: {
                              header: "",
                              title:
                                "Ubicá el DORSO de tu documento en el rectángulo",
                              short: "Encuadrá el DORSO del documento"
                            }
                          }
                        }
                      },
                      success: {
                        obverse: { card: { title: "¡Listo!" } },
                        reverse: { card: { title: "¡Listo!" } }
                      }
                    },
                    reviewImage: {
                      active: true,
                      card: {
                        title:
                          "Verificá que se vean todos los datos y no haya reflejos"
                      }
                    },
                    feedback: {
                      errors: {
                        videoErrorPermission: {
                          card: {
                            title:
                              "Por favor, habilitá los permisos de cámara y microfono en la configuración de tu navegador"
                          }
                        },
                        orientationChangedRestartMessage: {
                          card: {
                            title:
                              "Hubo un error por un cambio de orientación inesperado de tu dispositivo. Por favor, volvé a intentar"
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          ]
        },
        selfie: {
          liveness: "active",
          challengeLength: 2,
          setup: {
            generic: {
              common: {
                ui: {
                  buttons: { close: { show: true } }
                }
              }
            },
            sdkSelfie: {
              views: {
                instructions: {
                  active: true,
                  buttons: { startProcess: { text: "Empezar" } },
                  card: {
                    title: {
                      head: ["Mové tu cabeza hacia el lado indicado"]
                    },
                    subtitle: {
                      head: [
                        "Quedate en la posición indicada hasta que suene el sonido"
                      ]
                    }
                  }
                },
                reviewImage: {
                  active: true,
                  card: {
                    title:
                      "Verificá que se vea bien tu rostro y tenga una expresión natural"
                  }
                },
                capture: {
                  successTick: { show: true },
                  head: {
                    card: {
                      title: {
                        movement: "",
                        left: "Mové tu cabeza hacia la IZQUIERDA",
                        right: "Mové tu cabeza hacia la DERECHA",
                        top: "Mové tu cabeza hacia ARRIBA",
                        bottom: "Mové tu cabeza hacia ABAJO",
                        back: "¡Bien! Volvé a mirar al CENTRO"
                      }
                    }
                  },
                  guidance: {
                    selfie: {
                      card: {
                        header: "Seguí las instrucciones",
                        title:
                          "Centrá tu rostro en el marco, y mirá hacia la cámara"
                      }
                    },
                    tooFar: { card: { title: "Acercate un poco" } },
                    tooClose: { card: { title: "Alejate un poco" } },
                    centered: {
                      card: {
                        title: "Mantené la cámara estable y no sonrias"
                      }
                    }
                  }
                },
                feedback: {
                  errors: {
                    validation: {
                      card: {
                        title:
                          "No pudimos analizar correctamente el rostro. Por favor, volvé a intentar",
                        buttons: { restart: { text: "Reintentar" } }
                      }
                    },
                    orientationChangedRestartMessage: {
                      card: {
                        title:
                          "Hubo un error por un cambio de orientación inesperado de tu dispositivo. Por favor, volvé a intentar"
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
};

const ASO = {
  /**
   * Obtener token de seguridad ASO
   * @param none
   * @returns {Promise} Axios promise con el token
   */
  async obtenerTsec() {
    return axios
      .post(
        `${ASO_API_BASE}/TechArchitecture/mx/grantingTicket/V02`,
        {
          authentication: {
            userID: "ZG16027",
            consumerID: "16000027",
            authenticationType: "61",
            authenticationData: []
          },
          backendUserRequest: {
            userId: "",
            accessCode: "16000027",
            dialogId: ""
          }
        },
        {
          withCredentials: true
        }
      )
      .then(response => {
        return response;
      });
  },

  /**
   * Obtener URL de IFrame de Veridas
   * @param {Object} payload
   * @param {String} token Authorization token
   * @returns {Promise} Axios promise
   */
  async getVeridasIframe(payload, config) {
    veridasConfigData.flowId = getFlowIdByCamp(_BBVA_MOTOR);
    const body = JSON.parse(JSON.stringify(veridasConfigData));

    return axios.post(
      `${ASO_API_BASE}/documents-verifications/v3/validations`,
      {
        ...body,
        ...payload
      },
      config
    );
  },

  /**
   * Obtener resultados de la validacion Veridas
   * @param {String} payload VerificationID
   * @param {String} config Configuración adicional para la solicitud
   * @returns {Promise} Axios promise con el token
   */
  async getVeridasResult(payload, config) {
    return axios.get(
      `${ASO_API_BASE}/documents-verifications/v3/validations/${payload}?flowId=${getFlowIdByCamp(
        _BBVA_MOTOR
      )}`,
      config
    );
  },

  /**
   * Obtener resultados de la validación Renaper
   * @param {String} payload VerificationID
   * @param {Object} config Configuración adicional para la solicitud
   * @returns {Promise} Axios promise con los resultados de la validación
   */
  async getRenaperValidation(payload, config) {
    return axios.get(
      `${ASO_API_BASE}/documents-verifications-api-local/v0/validations/${payload}`,
      config
    );
  }
};

/**
 * Functión wrapper para generar tsec y llamar a una función de la API
 * @param {Function} apiCallFunc Función de la API a llamar
 * @param {Object} payload Payload para la función de la API
 * @returns {Promise} Axios promise con la respuesta de la API
 */
const withTsecAuth = async (apiCallFunc, payload) => {
  const currentTime = new Date().getTime();
  const dataAuthASO = store.getters.getDataAuthASO();

  // Verificar si ya tenemos un tsce y si no ha expirado
  if (dataAuthASO.tsec && dataAuthASO.tsecExpiry > currentTime) {
    return apiCallFunc.call(this, payload, {
      headers: {
        tsec: dataAuthASO.tsec
      }
    });
  }

  // Tsec expiró o no existe, obtener uno nuevo
  return ASO.obtenerTsec()
    .then(response => {
      const tsec = response.headers.tsec;
      const tsecExpiry = currentTime + ASO_TSEC_EXPIRATION;
      if (response.data.authenticationResponse.authenticationState === "OK") {
        store.dispatch(DO_DATA_AUTH_ASO, {
          tsec,
          tsecExpiry
        });

        return apiCallFunc.call(this, payload, {
          headers: {
            tsec
          }
        });
      } else {
        throw new Error(
          `Error en la autenticación de TSEC: ${response.data.authenticationResponse.authenticationState}`
        );
      }
    })
    .catch(error => {
      throw new Error(`Error en el servidor al obtener TSEC: ${error}`);
    });
};

export default ASO;
export { withTsecAuth, getFlowIdByCamp };
